import * as React from 'react';
import { cn } from '@/lib/utils';
import { useSpotlight } from '@/hooks/use-spotlight';
import { Card } from './card';

interface GlassCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Enable the interactive treatment: hover lift + scale, a cursor-tracking
   * spotlight and a specular sheen sweep. Motion is disabled under
   * `prefers-reduced-motion` (handled in CSS).
   */
  interactive?: boolean;
}

/**
 * Frosted-glass card. Thin wrapper over `<Card variant="glass">` that
 * wires up the optional interactive hover. Reusable design-system primitive; the
 * Dashboard module is currently its only consumer. Adds a named `group/glass`
 * so descendants opt in via `group-hover/glass:*`, without colliding with the
 * unnamed row-level `group` widgets use for per-row hover.
 */
const GlassCard = React.forwardRef<HTMLDivElement, GlassCardProps>(
  ({ interactive = false, className, onPointerMove, ...props }, ref) => {
    const handleSpotlight = useSpotlight(interactive);

    return (
      <Card
        ref={ref}
        variant="glass"
        className={cn(interactive && 'group/glass glass-surface--interactive', className)}
        onPointerMove={
          interactive || onPointerMove
            ? (event) => {
                handleSpotlight(event);
                onPointerMove?.(event);
              }
            : undefined
        }
        {...props}
      />
    );
  },
);
GlassCard.displayName = 'GlassCard';

export { GlassCard };
